import React, { useState, useCallback, useEffect } from 'react';
import { Input } from 'antd';
import cx from 'classnames';
import { isNil } from 'lodash';
import { setTrim } from '../../../lib/common';
import { REX_EXCEPT_NUMBER } from '../../../lib/rex';
import './TableFilterInput.js.scss';

/**
 *  react hook 을 사용하여
 *  함수형 component 에서도 상태를 제어할 수 있도록 함
 *
 *  *** useCallback ***
 *  useCallback 를 사용하여, useCallback 의 두 번째 인자값의
 *  변경이 있을 경우에만 함수를 새롭게 선언하도록 함
 *  빈 배열로 넘기는 경우, 함수 컴포넌트가 처음에 생성이 될 때만
 *  선언이 됨
 */
const TableFilterInput = (props) => {
  const { className, option, onFilter, type, disabled } = props;

  /**
   *  함수형 컴포넌트에서 사용될 state 초기화
   *  여기서 isEdit 은 value 와 modifiedValue 를 비교해서
   *  같으면 false, 다르면 true 로 업데이트 됨
   *  즉, isEdit 가 true 면 input element 의 background color 를 변경함으로써
   *  해당 필터는 수정 중이라는 상태를 사용자에게 알리게 됨
   */
  const [isFocus, setIsFocus] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [value, setValue] = useState(null);
  const [modifiedValue, setModifiedValue] = useState(null);

  /**
   *  `option` props 가 변경이 있을 때, 실행됨.
   *   value 의 option 값으로 업데이트 함
   */
  useEffect(() => {
    setValue(option);
  }, [option]);

  /**
   *  input onchange event 가 발생을 할 때마다
   *  modifiedValue 를 업데이트 함
   *  onchange 로 인해 modifiedValue 값이 변경이 되면,
   *  modifiedValue 와 value 값은
   */
  const onChange = useCallback(
    (e) => {
      let target = null;
      if (e.target.value !== '' && !isNil(e.target.value)) {
        if (type === 'number') {
          target = e.target.value.replace(REX_EXCEPT_NUMBER, '');
        } else {
          target = e.target.value;
        }
      }

      setModifiedValue(target || null);
      setIsEdit(true);
    },
    [type],
  );

  /**
   *  input element 의 focus 가 잡힌 상태에서
   *  `enter` 키를 누를 때마다, value 를 업데이트함
   *  그리고 onFilter 메소드를 통하여 서버로
   *  필터 옵션 값을 보냄
   */
  const onPressEnter = useCallback(
    (e) => {
      let target = null;
      if (e.target.value !== '' && !isNil(e.target.value)) target = setTrim(e.target.value);

      onFilter(target);
      setValue(target);
    },
    [onFilter],
  );

  /**
   *  input onblur event 가 발생을 할 때마다
   *  isFocus 값을 false 로 업데이트함
   *  value 와 modifiedValue 의 값이 같으면
   *  isEdit 은 false 로 업데이트함
   */
  const onBlur = useCallback(() => {
    if (value === modifiedValue) setIsEdit(false);

    setIsFocus(false);
  }, [value, modifiedValue]);

  /**
   *  modifiedValue 와 value 의 값이 다르고
   *  input element 가 focus 가 잡힌 상태라면
   *  수정중인 값 (modifiedValue) 을 사용자에게 보여줌
   */
  const targetValue = isFocus && isEdit ? modifiedValue : value;

  return (
    <Input
      className={cx('table-filter-input', className, { isEdit: isEdit })}
      value={targetValue}
      onFocus={() => setIsFocus(true)}
      onBlur={onBlur}
      onPressEnter={onPressEnter}
      onChange={onChange}
      size="large"
      disabled={disabled}
    />
  );
};

export default TableFilterInput;
